import React, { Component } from 'react';
import styles from './map.module.scss';
import mapboxgl, { LngLatLike, LngLat, Marker, LngLatBounds } from 'mapbox-gl';
import { postcodes } from './postcodes';
import CONFIG from '../../config';
import { MapKey } from '../map-key';
import { StateContext } from '../state';
import { GeoLocationPosition, LocationMarker, MarkerSet } from '../../interfaces';
import posed from 'react-pose';
import { navigate } from '@reach/router';
import { TextLink } from '../text-link';
// import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
const MapboxGeocoder = require('@mapbox/mapbox-gl-geocoder'); // @FIXME sort out proper mapbox geocoder typings

mapboxgl.accessToken = CONFIG.mapboxApiKey;

interface MapProps {
  height: string;
  width: string;
  center?: LngLatLike;
  zoom?: number;
  markers?: LocationMarker[];
  activeMarkerSet?: MarkerSet;
  activeMarkerType?: string;
  activeYear?: number;
  geolocation?: GeoLocationPosition;
  onGeolocation?: (place: any) => void;
  onNoResults?: () => void;
  geolocationIntro?: boolean;
  isDesktop?: boolean;
  browserNavBarHeight: number;
  onSkipSearch?: () => void;
}

interface MarkerArrayProps {
  instance: Marker;
  el: HTMLDivElement;
  data: LocationMarker;
}

const MapHolder = posed.div({
  mobileInitial: {
    width: '100vw',
    height: ({ heightWidth }: any) => heightWidth,
  },
  mobileNavBarVisible: {
    width: '100vw',
    height: ({ heightWidth }: any) => heightWidth,
  },
  mobileSidePanelOpen: {
    width: '100vw',
    height: ({ heightWidth }: any) => heightWidth,
  },
  desktopInitial: {
    width: ({ heightWidth }: any) => heightWidth,
    height: '100vh',
  },
  desktopNavBarVisible: {
    width: ({ heightWidth }: any) => heightWidth,
    height: '100vh',
  },
  desktopSidePanelOpen: {
    width: ({ heightWidth }: any) => heightWidth,
    height: '100vh',
  },
});

const ErrorMesssage = posed.div({
  visible: {
    opacity: 1,
    delay: 1000,
    applyAtStart: {
      visibility: 'visible',
    },
  },
  hidden: {
    opacity: 0,
    applyAtEnd: {
      visibility: 'hidden',
    },
  },
});

export class Map extends Component<MapProps> {
  public static defaultProps = {
    zoom: 10,
    height: null,
    width: null,
    center: [115.8605, -31.9505], // center of perth
  };
  public static contextType = StateContext;
  public map: any | mapboxgl.Map;
  public mapHolder = React.createRef<HTMLDivElement>();
  public mapEl = React.createRef<HTMLDivElement>();
  public geolocationEl = React.createRef<HTMLDivElement>();
  public markers: MarkerArrayProps[] = [];

  public state = {
    errorMessage: false,
  };

  public componentDidMount = () => {
    this.setMapHeight();

    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/water-corporation/cjw8mwmpd5sti1dnt5he88haw',
      zoom: this.props.zoom,
      center: this.props.center,
      logoPosition: 'bottom-right',
      // Lock bounds to WA region
      maxBounds: [[113, -35], [119, -30]],
    });

    if (this.props.geolocation) {
      this.initGeocoder();
    }

    this.map.on('load', () => {
      if (this.mapEl.current) {
        this.mapEl.current.style.backgroundImage = 'none';
        this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
      }
    });

    if (this.props.markers) {
      this.props.markers.forEach(marker => {
        this.addMarker(marker);
      });
    }

    if (this.props.markers && this.props.activeMarkerType) {
      this.panToMarkerType(this.props.activeMarkerType);
    }

    if (this.props.activeYear) {
      this.filterToYear(this.props.activeYear);
    }
  };

  public componentDidUpdate(previousProps: MapProps) {
    // Filter to year
    if (this.props.activeYear && this.props.activeYear !== previousProps.activeYear) {
      this.filterToYear(this.props.activeYear);
    }

    // Filter to active marker
    if (
      this.props.markers &&
      this.props.activeMarkerType &&
      this.props.activeMarkerType !== previousProps.activeMarkerType
    ) {
      this.filterToActiveMarkerType(this.props.activeMarkerType);
    }

    // Swap out markers when the activeMarkerSet changes
    if (this.props.activeMarkerSet !== previousProps.activeMarkerSet) {
      // Get the home marker
      const homeMarker = this.markers.find(marker => marker.data.type === 'home');

      // Remove all the markers that aren't the home marker
      this.markers
        .filter(marker => marker.data.type !== 'home')
        .forEach(marker => {
          marker.instance.remove();
        });

      // Reset the markers array
      if (homeMarker) {
        this.markers = [homeMarker];
      } else {
        this.markers = [];
      }

      // Add the new markers
      if (this.props.markers) {
        this.props.markers.forEach(marker => {
          this.addMarker(marker);
        });
      }
    }

    // Pan to active marker type when map props change
    setTimeout(() => {
      if (this.props.activeMarkerType) {
        this.panToMarkerType(this.props.activeMarkerType);
      }
    }, 500);
  }

  public filterToActiveMarkerType(type: string) {
    if (!this.markers) {
      return;
    }

    this.markers.forEach(marker => {
      if (type === 'all' || type === 'home') {
        this.showMarker(marker);
        return;
      }

      if (marker.data.type !== type && marker.data.type !== 'home') {
        this.hideMarker(marker);
      } else {
        this.showMarker(marker);
      }
    });
  }

  public resizeMap = () => {
    this.map.resize();
  };

  public render = () => {
    const position = this.props.geolocation ? `geolocation--${this.props.geolocation}` : '';
    const geolocation = (
      <div className={`${styles.geolocation} ${styles[position]}`} ref={this.geolocationEl}>
        {this.props.geolocationIntro && (
          <div className={styles.geolocationIntro}>
            <h1>
              Perth's <span>Water</span> Supply
            </h1>
            <h2>
              <mark>Where does Perth's</mark>
              <mark>drinking water</mark>
              <mark>come from?</mark>
            </h2>
            <p>Ever wondered where your water comes from? Or how climate change has affected our supply?</p>
            <p>Enter your address to see water sources around your home.</p>
            <p className={styles.addressHelp}>
              Can't see your address? <button onClick={this.props.onSkipSearch}>Skip search</button>.
            </p>
          </div>
        )}

        <ErrorMesssage className={styles.errorMessage} pose={this.state.errorMessage ? 'visible' : 'hidden'}>
          <button onClick={() => this.setState({ errorMessage: false })} aria-label="Close">
            x
          </button>
          Sorry, this tool isn't available for your location. For information about our water supply please{' '}
          <TextLink href="https://www.watercorporation.com.au/water-supply">visit our website</TextLink>.
        </ErrorMesssage>
      </div>
    );

    return (
      <>
        <MapHolder
          className={`${styles.mapHolder} ${this.props.geolocationIntro ? styles.geolocationIntroVisible : ''}`}
          pose={this.mapHeightWidth.poseState}
          heightWidth={this.mapHeightWidth.heightWidthValue}
          poseKey={this.mapHeightWidth.heightWidthValue}
          ref={this.mapHolder}
          onPoseComplete={this.resizeMap}
        >
          <div id="overlayGrid" className={styles.overlayGrid}>
            {this.props.geolocation && geolocation}
            {this.props.markers && <MapKey markers={this.props.markers} />}
          </div>
          <div id="map" className={styles.map} ref={this.mapEl} />
        </MapHolder>
      </>
    );
  };

  public initGeocoder = () => {
    if (!this.geolocationEl.current) {
      return;
    }

    const bbox = new LngLatBounds();
    postcodes.forEach(postcode => {
      if (typeof postcode.long === 'number' && typeof postcode.lat === 'number') {
        const pos = new LngLat(postcode.long, postcode.lat);
        bbox.extend(pos);
      }
    });
    const b = bbox.toArray();

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      countries: 'au',
      bbox: [b[0][0], b[0][1], b[1][0], b[1][1]],
      filter: this.filterPostcodes,
      flyTo: false,
      placeholder: 'My address',
      marker: false,
    });

    this.geolocationEl.current.appendChild(geocoder.onAdd(this.map));

    const inputEl = this.geolocationEl.current.querySelector('.mapboxgl-ctrl-geocoder--input') as HTMLInputElement;
    inputEl.focus();
    inputEl.setAttribute('aria-label', 'Address search');

    geocoder.on('results', ({ features }: any) => {
      if (features.length === 0) {
        if (this.props.onNoResults) {
          this.props.onNoResults();
        }
        this.setState({
          errorMessage: true,
        });

        // Tracking
        window.digitalData.form.errorList = 'location not available';
        window.dataLayer.push({
          event: 'formValidationError',
        });
      } else {
        this.setState({
          errorMessage: false,
        });
      }
    });

    geocoder.on('result', ({ result }: any) => {
      const marker: LocationMarker = {
        location: result.center,
        type: 'home',
        title: 'Home',
        desc: 'We all need to adapt to protect our water sources.',
        link: '',
        year: Infinity,
      };

      this.removeMarkerType('home');

      const m = this.addMarker(marker);
      this.hideMarker(m, true).then(() => {
        this.showMarker(m);
      });

      this.panToMarkerType('all');

      if (this.props.onGeolocation) {
        this.props.onGeolocation(result);
      }

      // Blur search input after geolocation
      const input = document.activeElement as HTMLInputElement;
      if (input) {
        input.blur();
      }

      // Tracking
      const suburb = result.context.find((item: any) => item.id.includes('locality')) || null;
      window.digitalData.form.location = (suburb && suburb.text) || '';
      window.dataLayer.push({
        event: 'addressFormSubmitted',
      });

      this.setState({
        errorMessage: false,
      });
    });
  };

  public filterPostcodes = (item: any) => {
    let allowed = false;
    Object.keys(item.context).forEach(key => {
      const context = item.context[key];
      if (context.id.includes('postcode')) {
        const pc = parseInt(context.text, 10);
        if ((pc >= 6000 && pc < 6200) || pc === 6210) {
          allowed = true;
        }
      }
    });
    return allowed;
  };

  public addMarker = (marker: LocationMarker) => {
    const markerEl = document.createElement('div');
    const markerContentEl = document.createElement('div');

    markerEl.classList.add('map-marker');
    markerEl.classList.add(marker.type);

    const m = new mapboxgl.Marker(markerEl);

    const html = `
      <h3>${marker.title}</h3>
      <p>${marker.desc}</p>
    `;

    markerContentEl.innerHTML = html;

    // Tracking on click of marker
    markerEl.addEventListener('click', () => {
      window.digitalData.tool.name = 'PWS Location Map';
      window.digitalData.tool.step = marker.title;
      window.digitalData.tool.markerType = marker.type;
      window.dataLayer.push({
        event: 'mapInteraction',
      });
    });

    // Button behaviour
    if ((marker.link && marker.link !== '') || marker.type === 'home') {
      const markerButtonEl = document.createElement('a');
      markerButtonEl.classList.add('button', 'small');

      if (marker.type === 'home') {
        markerButtonEl.innerText = 'find out how';
      } else {
        markerButtonEl.innerText = 'find out more';
      }

      if (marker.link) {
        markerButtonEl.setAttribute('href', marker.link);
      }

      // External links
      if (marker.link && marker.link.includes('http')) {
        markerButtonEl.setAttribute('target', '_blank');
        markerButtonEl.setAttribute('rel', 'noopener noreferrer');
      } else {
        // Internal links
        markerButtonEl.addEventListener('click', e => {
          e.preventDefault();

          if (marker.link) {
            navigate(marker.link);
          }

          if (marker.type === 'home') {
            navigate('/how');
          }

          popup.remove();
        });
      }

      markerContentEl.appendChild(markerButtonEl);
    }

    const popup = new mapboxgl.Popup({ offset: 25 }).setDOMContent(markerContentEl);

    m.setLngLat(marker.location);
    m.setPopup(popup);
    m.addTo(this.map);

    const markerObj = {
      instance: m,
      el: markerEl,
      data: marker,
    };

    this.markers.push(markerObj);

    return markerObj;
  };

  public panToMarkerType = (type: string) => {
    if (!this.markers.length) {
      return;
    }

    const homeMarker = this.markers.find(marker => marker.data.type === 'home');

    const bounds = new mapboxgl.LngLatBounds();
    this.markers.forEach(marker => {
      if (marker.data.type === type || marker.data.type === 'home' || type === 'all') {
        bounds.extend(marker.data.location as LngLat);
      }
    });

    switch (type) {
      // Fly to home marker instead of fit bounds
      case 'home':
        setTimeout(() => {
          if (homeMarker && homeMarker.data.location) {
            this.map.flyTo({
              center: homeMarker.data.location,
              zoom: 11,
            });
          }
        }, 500);
        break;
      // Fly to groundwarer replen when there's no home marker
      case 'groundwater-replenishment':
        if (!homeMarker) {
          this.map.flyTo({
            center: bounds.getCenter(),
            zoom: 10,
          });
        } else {
          this.map.fitBounds(bounds, {
            padding: this.props.isDesktop ? 200 : 80,
          });
        }
        break;
      case 'all':
        this.map.fitBounds(bounds, {
          padding: -100,
        });
        break;
      default:
        this.map.fitBounds(bounds, {
          padding: this.props.isDesktop ? 200 : 80,
        });
        break;
    }

    // Close all popups
    this.markers.forEach(marker => marker.instance.getPopup().remove());
  };

  public filterToYear = (year: number) => {
    if (!this.markers) {
      return;
    }

    this.markers.forEach(marker => {
      if (marker.data.type !== 'home' && marker.data.year > year) {
        this.hideMarker(marker);
      } else {
        this.showMarker(marker);
      }
    });
  };

  public hideMarker = (marker: MarkerArrayProps, instant?: boolean) => {
    return new Promise(resolve => {
      if (instant) {
        marker.el.classList.add('invisible');
        marker.el.classList.add('hidden');
        setTimeout(() => {
          marker.el.classList.remove('invisible');
          resolve();
        }, 400);
      } else {
        marker.el.classList.add('hidden');
      }
    });
  };

  public showMarker = (marker: MarkerArrayProps) => {
    return new Promise(resolve => {
      if (marker.el.classList.contains('hidden')) {
        marker.el.classList.remove('hidden');
        marker.el.classList.add('shown');
        setTimeout(() => {
          marker.el.classList.remove('shown');
          resolve();
        }, 400);
      }
    });
  };

  public removeMarkerType(type: string) {
    this.markers = this.markers.filter(marker => {
      if (marker.data.type === type) {
        this.hideMarker(marker).then(() => {
          marker.instance.remove();
        });
        return null;
      }
      return marker;
    });
  }

  private setMapHeight() {
    if (this.mapHolder.current) {
      // Explicit values passed in as props
      this.mapHolder.current.style.width = this.props.width ? `${this.props.width}px` : null;
      this.mapHolder.current.style.height = this.props.height ? `${this.props.height}px` : null;
    }
  }

  // Returns the height OR width of the map based on the current breakpoint used for pose animation
  private get mapHeightWidth() {
    const [{ navBarVisible, sidePanelOpen }] = this.context;
    const { isDesktop } = this.props;

    // Default values and state for switching between pose animations
    let heightWidthValue = isDesktop ? `100vw` : `calc(100vh - ${this.props.browserNavBarHeight}px)`;
    let poseState = isDesktop ? 'desktopInitial' : 'mobileInitial';

    if (navBarVisible) {
      poseState = isDesktop ? 'desktopNavBarVisible' : 'mobileNavBarVisible';
      heightWidthValue = isDesktop
        ? `calc(100vw - ${styles.navBar})`
        : `calc(100vh - ${this.props.browserNavBarHeight + parseFloat(styles.navBar)}px)`;
    }
    if (sidePanelOpen) {
      poseState = isDesktop ? 'desktopSidePanelOpen' : 'mobileSidePanelOpen';
      heightWidthValue = isDesktop
        ? `calc(100vw - ${parseFloat(styles.navBar) + parseFloat(styles.sidePanelWidth)}px)`
        : `calc(${styles.sidePanelPadding} - ${parseFloat(styles.navBar)}px)`;
    }

    return {
      poseState,
      heightWidthValue,
    };
  }
}
